import React, {PureComponent} from 'react';
import styled from 'styled-components';

import SideModal from './SideModal';
import Button from './Button';
import Input from './Input';
import Icon from './Icon';
import { getTagName } from '../lib/tag-helpers';

class TagManagementModal extends PureComponent {
  state = {
    newTagName: '',
    selectedTags: new Set((this.props.item && this.getTagNames(this.props.item.tags)) || []),
    availableTags: [...this.props.availableTags],
    deleteConfirmTag: null,
    tagsToDelete: new Set()
  };

  getTagNames = (tags) => {
    if (!tags) return [];
    return tags.map(getTagName);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.item !== this.props.item) {
      this.setState({
        selectedTags: new Set((this.props.item && this.getTagNames(this.props.item.tags)) || []),
        newTagName: '',
        availableTags: [...this.props.availableTags],
        tagsToDelete: new Set()
      });
    }
    if (prevProps.availableTags !== this.props.availableTags) {
      this.setState({
        availableTags: [...this.props.availableTags]
      });
    }
  }

  handleNewTagChange = e => {
    this.setState({newTagName: e.target.value});
  };

  handleTagToggle = tagName => {
    this.setState(prevState => {
      const newSelectedTags = new Set(prevState.selectedTags);
      if (newSelectedTags.has(tagName)) {
        newSelectedTags.delete(tagName);
      } else {
        newSelectedTags.add(tagName);
      }
      return {selectedTags: newSelectedTags};
    });
  };

  handleCreateAndApplyTag = () => {
    const {newTagName, availableTags} = this.state;
    const trimmedTag = newTagName.trim();
    if (trimmedTag && trimmedTag.length <= 100) {
      const tagExists = availableTags.some(tag => getTagName(tag) === trimmedTag);
      this.setState(prevState => ({
        selectedTags: new Set([...prevState.selectedTags, trimmedTag]),
        availableTags: tagExists ? availableTags : [...availableTags, { name: trimmedTag, id: null }],
        newTagName: ''
      }));
    }
  };

  handleDeselectAll = () => {
    this.setState({selectedTags: new Set()});
  };

  handleSave = () => {
    const {onSave, item} = this.props;
    const {selectedTags, availableTags, tagsToDelete} = this.state;
    onSave(item, Array.from(selectedTags), availableTags, Array.from(tagsToDelete));
  };

  handleDeleteTag = async (tag) => {
    this.setState({deleteConfirmTag: tag});
  };

  handleConfirmDelete = () => {
    const {deleteConfirmTag, availableTags, selectedTags, tagsToDelete} = this.state;
    
    if (deleteConfirmTag) {
      const tagName = getTagName(deleteConfirmTag);
      const tagId = typeof deleteConfirmTag === 'string' ? null : deleteConfirmTag.id;

      const updatedTagsToDelete = new Set(tagsToDelete);
      if (tagId) {
        updatedTagsToDelete.add(deleteConfirmTag);
      }
      
      const updatedAvailableTags = availableTags.filter(tag => 
        getTagName(tag) !== tagName
      );
      
      const updatedSelectedTags = new Set(Array.from(selectedTags).filter(selectedTagName => 
        selectedTagName !== tagName
      ));
      
      this.setState({
        availableTags: updatedAvailableTags,
        selectedTags: updatedSelectedTags,
        tagsToDelete: updatedTagsToDelete,
        deleteConfirmTag: null
      });
    }
  };

  handleCancelDelete = () => {
    this.setState({deleteConfirmTag: null});
  };

  getDeleteConfirmationMessage = () => {
    const {entityType} = this.props;
    const tagName = getTagName(this.state.deleteConfirmTag);
    
    let entityText;
    if (entityType === 'user') {
      entityText = 'users';
    } else {
      entityText = 'forms and reports';
    }
    
    return `Are you sure you want to delete the tag "${tagName}"?\n\nThis will remove the tag from all ${entityText} where it's currently applied.`;
  };

  render() {
    const {open, onClose, item, itemName} = this.props;
    const {newTagName, selectedTags, availableTags} = this.state;

    if (!item) return null;

    const displayName = itemName || item.name || item.userName || 'Item';

    return (
      <SideModal
        open={open}
        onClose={onClose}
        title={`Modify Tags`}
        header={<ItemName>"{displayName}"</ItemName>}
        options={[
          <Button onClick={onClose} tertiary>Cancel</Button>,
          <Button onClick={this.handleSave}>Save Changes</Button>
        ]}
      >
        <Content>
          <Section>
            <SectionTitle>Create New Tag</SectionTitle>
            <NewTagContainer>
              <Input
                id="new-tag-input"
                placeholder="Enter tag name..."
                value={newTagName}
                maxLength={100}
                onChange={this.handleNewTagChange}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    this.handleCreateAndApplyTag();
                  }
                }}
              />
              <Button
                onClick={this.handleCreateAndApplyTag}
                disabled={!newTagName.trim() || newTagName.trim().length > 100}
                style={{marginLeft: '0.5rem'}}
              >
                Add Tag
              </Button>
            </NewTagContainer>
            {newTagName && (
              <CharacterCount isOverLimit={newTagName.length > 100}>
                {newTagName.length}/100 characters
              </CharacterCount>
            )}
          </Section>

          <Section>
            <SectionTitle>Available Tags</SectionTitle>
            <TagsGrid>
              {availableTags.map(tag => {
                const tagName = getTagName(tag);
                return (
                  <TagOption
                    key={tagName}
                    selected={selectedTags.has(tagName)}
                    title={tagName}
                  >
                    <TagContent onClick={() => this.handleTagToggle(tagName)}>
                      <TagName>{tagName}</TagName>
                      <TagActions>
                        <CheckboxArea>
                          {selectedTags.has(tagName) && <Icon name="check" size="1em" />}
                        </CheckboxArea>
                        <DeleteTagButton
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.handleDeleteTag(tag);
                          }}
                          title="Delete tag"
                        >
                          <Icon name="close" size="0.75em" />
                        </DeleteTagButton>
                      </TagActions>
                    </TagContent>
                  </TagOption>
                );
              })}
            </TagsGrid>
          </Section>

          {selectedTags.size > 0 && (
            <Section>
              <SectionHeader>
                <SectionTitle>Selected Tags ({selectedTags.size})</SectionTitle>
                <Button tertiary onClick={this.handleDeselectAll}>
                  Deselect All
                </Button>
              </SectionHeader>
              <SelectedTagsContainer>
                {Array.from(selectedTags).map(tag => (
                  <SelectedTag key={tag} title={tag}>
                    <SelectedTagText>{tag}</SelectedTagText>
                    <RemoveTagButton
                      onClick={() => this.handleTagToggle(tag)}
                    >
                      <Icon name="close" size="0.875em" />
                    </RemoveTagButton>
                  </SelectedTag>
                ))}
              </SelectedTagsContainer>
            </Section>
          )}
        </Content>
        
        {this.state.deleteConfirmTag && (
          <ConfirmOverlay>
            <ConfirmDialog>
              <ConfirmTitle>Delete Tag</ConfirmTitle>
              <ConfirmMessage>
                {this.getDeleteConfirmationMessage().split('\n').map((line, index) => (
                  <span key={index}>
                    {line.includes('"') ? (
                      <>
                        {line.split('"')[0]}
                        <strong>"{line.split('"')[1]}"</strong>
                        {line.split('"')[2]}
                      </>
                    ) : (
                      line
                    )}
                    {index < this.getDeleteConfirmationMessage().split('\n').length - 1 && <br />}
                  </span>
                ))}
              </ConfirmMessage>
              <ConfirmButtons>
                <Button tertiary onClick={this.handleCancelDelete}>Cancel</Button>
                <Button danger onClick={this.handleConfirmDelete}>Delete Tag</Button>
              </ConfirmButtons>
            </ConfirmDialog>
          </ConfirmOverlay>
        )}
      </SideModal>
    );
  }
}

const Content = styled.div`
  padding: 1.875rem;
`;

const ItemName = styled.div`
  font-size: 1.125rem;
  font-weight: 500;
  color: #666;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 500;
  margin: 0 0 1rem 0;
  color: #333;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  ${SectionTitle} {
    margin: 0;
  }
`;

const NewTagContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
`;

const TagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const TagOption = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.selected ? '#666' : '#ddd'};
  background-color: ${props => props.selected ? '#f0f0f0' : 'white'};
  color: ${props => props.selected ? '#333' : '#666'};
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  min-height: 3rem;
  
  &:hover {
    border-color: #999;
    background-color: #f8f8f8;
    
    .delete-button {
      opacity: 1;
    }
  }
`;

const TagContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  flex: 1;
  min-width: 0;
`;

const TagActions = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  position: relative;
  width: 2rem;
  height: 1.25rem;
  overflow: hidden;
`;

const TagName = styled.span`
  font-weight: 500;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  word-break: break-word;
`;

const CheckboxArea = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: absolute;
  top: 0;
  right: 0;
  transition: transform 0.2s ease;
  
  ${TagOption}:hover & {
    transform: translateX(-1.75rem);
  }
`;

const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SelectedTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #666;
  color: white;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  max-width: 200px;
`;

const SelectedTagText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveTagButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  margin: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const CharacterCount = styled.div`
  font-size: 0.75rem;
  color: ${props => props.isOverLimit ? '#dc3545' : '#666'};
  margin-top: 0.25rem;
  text-align: left;
`;

const DeleteTagButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 50%;
  color: #dc3545;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: absolute;
  top: 0;
  right: 0;
  transform: translateX(100%);
  padding: 0;
  line-height: 1;
  
  svg {
    display: block;
    margin: auto;
  }
  
  &:hover {
    background: rgba(220, 53, 69, 0.2);
    border-color: rgba(220, 53, 69, 0.5);
  }
  
  ${TagOption}:hover &.delete-button {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ConfirmDialog = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ConfirmTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: #333;
`;

const ConfirmMessage = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 1.5rem 0;
  line-height: 1.4;
`;

const ConfirmButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
`;

export default TagManagementModal;